import { type ToolSpec, n, s, fmt, money } from "./types";

export const LIFESTYLE_TOOLS: ToolSpec[] = [
  {
    slug: "ev-fuel-trip-cost-splitter",
    cluster: "lifestyle",
    name: "EV & Fuel Trip Splitter",
    h1: "EV and Fuel Trip Cost Splitter Calculator (Cost Per Passenger)",
    title: "EV vs Fuel Trip Cost Calculator & Passenger Splitter",
    description:
      "Compare the cost of a journey by petrol, diesel or electric vehicle, split it between passengers, and see the per-mile and per-kilometre running cost.",
    keywords: [
      "ev fuel cost trip splitter calculator per passenger",
      "petrol vs electric car cost per mile calculator",
      "road trip fuel cost split between friends",
      "kwh per mile electricity cost for ev charging",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: [{ value: "$", label: "USD $" }, { value: "£", label: "GBP £" }, { value: "€", label: "EUR €" }, { value: "A$", label: "AUD A$" }] },
      { id: "distance", label: "Trip distance", type: "number", def: "320", unit: "miles / km", min: 1 },
      { id: "type", label: "Vehicle type", type: "select", def: "petrol", options: [
        { value: "petrol", label: "Petrol / gasoline" },
        { value: "diesel", label: "Diesel" },
        { value: "ev", label: "Electric" },
        { value: "hybrid", label: "Hybrid" },
      ] },
      { id: "efficiency", label: "Efficiency (MPG, or mi/kWh for EV)", type: "number", def: "34", min: 1, step: 0.5 },
      { id: "price", label: "Fuel price per gallon / kWh price", type: "number", def: "3.65", min: 0.01, step: 0.05 },
      { id: "tolls", label: "Tolls and parking", type: "number", def: "24", min: 0, step: 1 },
      { id: "passengers", label: "People sharing the cost", type: "number", def: "4", min: 1, max: 20 },
      { id: "return", label: "Return journey", type: "select", def: "1", options: [{ value: "1", label: "One way" }, { value: "2", label: "Round trip" }] },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const dist = n(v, "distance") * n(v, "return", 1);
      const eff = Math.max(0.1, n(v, "efficiency", 1));
      const isEv = s(v, "type") === "ev";
      const energy = isEv ? dist / eff : dist / eff;   // kWh or gallons
      const fuelCost = energy * n(v, "price");
      const total = fuelCost + n(v, "tolls");
      const people = Math.max(1, n(v, "passengers", 1));
      return {
        cards: [
          { label: "Cost per person", value: money(total / people, c, 2), sub: `${people} sharing`, accent: true },
          { label: "Total trip cost", value: money(total, c, 2), sub: `${fmt(dist)} distance units` },
          { label: isEv ? "Electricity used" : "Fuel used", value: `${fmt(energy, 2)} ${isEv ? "kWh" : "gal"}`, sub: money(fuelCost, c, 2) },
          { label: "Cost per mile", value: money(total / Math.max(dist, 1), c, 3), sub: `Including ${money(n(v, "tolls"), c)} tolls` },
        ],
        table: {
          title: "Same trip in different vehicles",
          head: ["Vehicle", "Efficiency", "Energy used", "Energy cost", "Per person"],
          rows: [
            ["Efficient petrol", "45 MPG", `${fmt(dist / 45, 2)} gal`, money((dist / 45) * n(v, "price"), c, 2), money(((dist / 45) * n(v, "price") + n(v, "tolls")) / people, c, 2)],
            ["Average petrol", "30 MPG", `${fmt(dist / 30, 2)} gal`, money((dist / 30) * n(v, "price"), c, 2), money(((dist / 30) * n(v, "price") + n(v, "tolls")) / people, c, 2)],
            ["Large SUV", "20 MPG", `${fmt(dist / 20, 2)} gal`, money((dist / 20) * n(v, "price"), c, 2), money(((dist / 20) * n(v, "price") + n(v, "tolls")) / people, c, 2)],
            ["EV home charge", "3.5 mi/kWh", `${fmt(dist / 3.5, 1)} kWh`, money((dist / 3.5) * 0.15, c, 2), money(((dist / 3.5) * 0.15 + n(v, "tolls")) / people, c, 2)],
            ["EV rapid charge", "3.5 mi/kWh", `${fmt(dist / 3.5, 1)} kWh`, money((dist / 3.5) * 0.55, c, 2), money(((dist / 3.5) * 0.55 + n(v, "tolls")) / people, c, 2)],
          ],
        },
        summary: "Charging an EV at home is usually three to four times cheaper per mile than rapid charging on a motorway.",
      };
    },
    intro:
      "Trip cost has two components that behave very differently. Energy scales linearly with distance and inversely with efficiency, while tolls and parking are fixed regardless of how far you drive. Splitting between passengers turns a marginal-cost question into a fairness question, which is why the per-person figure matters more than the total.",
    sections: [
      { h: "EV charging costs are not one number", body: "Home overnight charging on an off-peak tariff can cost eight to fifteen cents per kilowatt-hour. Public rapid chargers routinely charge fifty to seventy cents — more than four times as much. For an EV averaging 3.5 miles per kilowatt-hour, that is the difference between four cents and sixteen cents per mile." },
      { h: "Efficiency degrades on the motorway", body: "Aerodynamic drag rises with the square of speed, so both combustion and electric vehicles lose efficiency above roughly sixty miles per hour. EVs are hit harder in relative terms because they have no waste heat to recover and cabin heating draws directly from the traction battery in cold weather, cutting winter range by up to thirty percent." },
      { h: "Splitting fairly with a car owner", body: "Fuel and tolls are the obvious shared costs. Many groups also add a per-mile allowance — commonly around ten to fifteen cents — to cover tyres, servicing and depreciation, which are real costs the driver absorbs alone. Agree this before departure rather than at the destination." },
    ],
    staticTable: {
      title: "Cost per mile by powertrain",
      head: ["Vehicle", "Efficiency", "Energy price", "Cost per mile", "Cost per 1,000 miles"],
      rows: [
        ["Compact petrol", "40 MPG", "$3.65/gal", "$0.091", "$91"],
        ["Mid-size petrol", "28 MPG", "$3.65/gal", "$0.130", "$130"],
        ["Large SUV petrol", "18 MPG", "$3.65/gal", "$0.203", "$203"],
        ["Diesel estate", "45 MPG", "$3.95/gal", "$0.088", "$88"],
        ["EV — home charge", "3.5 mi/kWh", "$0.15/kWh", "$0.043", "$43"],
        ["EV — public rapid", "3.5 mi/kWh", "$0.55/kWh", "$0.157", "$157"],
        ["Hybrid", "52 MPG", "$3.65/gal", "$0.070", "$70"],
      ],
    },
    faqs: [
      { q: "Is an EV cheaper to run than petrol?", a: "On home charging, substantially — typically four to five cents per mile against twelve to fourteen for petrol. On public rapid chargers the advantage largely disappears." },
      { q: "How should road trip fuel costs be split?", a: "Divide fuel plus tolls by the number of people including the driver. Many groups add a small per-mile wear allowance for the car owner." },
      { q: "Why does my EV range drop in winter?", a: "Cabin heating draws directly from the traction battery and cold cells have higher internal resistance. Combined, these commonly reduce range by twenty to thirty percent." },
    ],
    related: ["car-depreciation-calculator", "appliance-electricity-cost-calculator", "tip-and-bill-splitter", "solar-panel-battery-size-calculator"],
  },

  {
    slug: "recipe-scale-converter",
    cluster: "lifestyle",
    name: "Recipe Scale & Unit Converter",
    h1: "Recipe Multiplier Calculator (Ingredient Converter, Cups to Grams)",
    title: "Recipe Scaling Calculator — Cups to Grams Converter",
    description:
      "Scale any recipe up or down by servings and convert cup measures to grams by ingredient, with pan-size adjustment and baking-time guidance.",
    keywords: [
      "recipe multiplier calculator ingredient converter cups grams",
      "how to scale a recipe for more servings",
      "cups to grams flour sugar butter conversion",
      "adjust baking time when doubling a recipe",
    ],
    inputs: [
      { id: "original", label: "Original servings", type: "number", def: "4", min: 1, max: 100 },
      { id: "target", label: "Target servings", type: "number", def: "10", min: 1, max: 200 },
      { id: "ingredient", label: "Ingredient to convert", type: "select", def: "125", options: [
        { value: "125", label: "Plain flour — 125 g/cup" },
        { value: "120", label: "Bread flour — 120 g/cup" },
        { value: "200", label: "Granulated sugar — 200 g/cup" },
        { value: "220", label: "Brown sugar packed — 220 g/cup" },
        { value: "227", label: "Butter — 227 g/cup" },
        { value: "240", label: "Water / milk — 240 g/cup" },
        { value: "95", label: "Rolled oats — 95 g/cup" },
        { value: "180", label: "Rice uncooked — 180 g/cup" },
        { value: "85", label: "Cocoa powder — 85 g/cup" },
      ] },
      { id: "cups", label: "Amount in the original recipe", type: "number", def: "2", unit: "cups", min: 0.05, step: 0.25 },
    ],
    compute: (v) => {
      const factor = n(v, "target", 1) / Math.max(1, n(v, "original", 1));
      const gPerCup = n(v, "ingredient", 125);
      const cups = n(v, "cups");
      const scaledCups = cups * factor;
      const grams = scaledCups * gPerCup;
      const frac = (x: number) => {
        const whole = Math.floor(x);
        const r = x - whole;
        const eighths = Math.round(r * 8);
        const names = ["", "⅛", "¼", "⅜", "½", "⅝", "¾", "⅞", ""];
        if (eighths === 8) return `${whole + 1}`;
        return `${whole || ""}${whole && eighths ? " " : ""}${names[eighths]}` || "0";
      };
      return {
        cards: [
          { label: "Scaling factor", value: `${fmt(factor, 2)}×`, sub: `${n(v, "original")} → ${n(v, "target")} servings`, accent: true },
          { label: "New amount (cups)", value: frac(scaledCups), sub: `${fmt(scaledCups, 2)} cups exactly` },
          { label: "New amount (grams)", value: `${fmt(grams)} g`, sub: `${gPerCup} g per cup` },
          { label: "In ounces", value: `${fmt(grams / 28.35, 1)} oz`, sub: `${fmt(grams / 453.6, 2)} lb` },
        ],
        table: {
          title: "Common measures scaled by this factor",
          head: ["Original", "Scaled", "In grams (this ingredient)", "In tablespoons"],
          rows: [0.25, 0.5, 0.75, 1, 1.5, 2, 3].map((x) => [`${frac(x)} cup`, frac(x * factor), fmt(x * factor * gPerCup), fmt(x * factor * 16, 1)]),
        },
        summary: factor > 2
          ? "Scaling above 2× usually needs a larger pan and a longer, slightly cooler bake — see the guidance below."
          : "At this factor, keep the same pan depth and check for doneness a few minutes early.",
      };
    },
    intro:
      "Scaling a recipe by volume works fine for liquids and disastrously for flour, because a cup of flour can weigh anywhere from 120 to 150 grams depending on whether it was scooped or spooned. Converting to grams first removes that variance entirely, which is why every professional kitchen and every serious baking book works by weight.",
    sections: [
      { h: "What does not scale linearly", body: "Salt, spices, leavening and yeast should be scaled at roughly eighty to ninety percent of the mathematical factor when doubling or more, because their perceived intensity does not rise proportionally with volume. Baking soda and powder in particular can produce a soapy, metallic note when scaled straight up." },
      { h: "Pan size and bake time", body: "Doubling a batter needs roughly double the pan area, not double the depth — a deeper batter bakes unevenly and the outside overcooks before the centre sets. If the depth must increase, drop the oven temperature by about fifteen degrees Celsius and extend the time by twenty-five to fifty percent, testing from the original time onward." },
      { h: "Cup weights vary by ingredient and technique", body: "Spooned and levelled plain flour is about 125 g per cup; scooped straight from the bag it can reach 150 g, which is a twenty percent error in the ingredient that structures the entire bake. Brown sugar is measured packed, cocoa is sifted, and butter is best cut by weight rather than by stick markings." },
    ],
    staticTable: {
      title: "Cup to gram conversions by ingredient",
      head: ["Ingredient", "1 cup", "½ cup", "1 tbsp", "Notes"],
      rows: [
        ["Plain flour", "125 g", "63 g", "8 g", "Spooned and levelled"],
        ["Bread flour", "120 g", "60 g", "7.5 g", "Higher protein"],
        ["Granulated sugar", "200 g", "100 g", "12.5 g", "—"],
        ["Brown sugar", "220 g", "110 g", "13.8 g", "Firmly packed"],
        ["Butter", "227 g", "113 g", "14 g", "2 US sticks per cup"],
        ["Water / milk", "240 g", "120 g", "15 g", "1 g per ml"],
        ["Honey", "340 g", "170 g", "21 g", "Weigh — very dense"],
        ["Cocoa powder", "85 g", "43 g", "5.3 g", "Sifted"],
        ["Rolled oats", "95 g", "48 g", "6 g", "Loosely filled"],
      ],
    },
    faqs: [
      { q: "How do I scale a recipe for more servings?", a: "Divide target servings by original servings to get a factor, multiply every ingredient weight by it, then reduce salt, spices and leavening to about eighty-five percent of the scaled amount when the factor exceeds two." },
      { q: "How many grams is a cup of flour?", a: "About 125 g for plain flour spooned and levelled. Scooping directly from the bag compacts it to 145-150 g, which is enough to make a cake dry." },
      { q: "Do I need to change the baking time when doubling?", a: "Only if the batter depth changes. Same depth in a larger pan bakes in almost the same time; a deeper batter needs a lower temperature and a longer bake." },
    ],
    related: ["bakers-percentage-recipe-calculator", "coffee-to-water-ratio-calculator", "sourdough-hydration-calculator", "tip-and-bill-splitter"],
  },

  {
    slug: "bakers-percentage-recipe-calculator",
    cluster: "lifestyle",
    name: "Baker's Percentage Formula Builder",
    h1: "Baker's Percentage Calculator (Build Any Bread Formula by Weight)",
    title: "Baker's Percentage Calculator — Bread Formula Builder",
    description:
      "Enter a target dough weight and percentages to get exact flour, water, salt, yeast and levain weights, with dough-per-loaf and batch scaling.",
    keywords: [
      "sourdough bread hydration percentage calculator formula",
      "bakers percentage calculator total dough weight",
      "how much yeast per kg of flour",
      "scale bread recipe by number of loaves",
    ],
    inputs: [
      { id: "loaves", label: "Number of loaves", type: "number", def: "2", min: 1, max: 40 },
      { id: "loafweight", label: "Dough weight per loaf", type: "number", def: "900", unit: "g", min: 100, step: 50 },
      { id: "hydration", label: "Hydration", type: "number", def: "72", unit: "%", min: 50, max: 110 },
      { id: "saltpct", label: "Salt", type: "number", def: "2", unit: "%", min: 0, max: 4, step: 0.1 },
      { id: "levainpct", label: "Levain / preferment", type: "number", def: "20", unit: "%", min: 0, max: 60 },
      { id: "yeastpct", label: "Commercial yeast", type: "number", def: "0", unit: "%", min: 0, max: 3, step: 0.1 },
      { id: "wholepct", label: "Wholegrain flour share", type: "number", def: "20", unit: "%", min: 0, max: 100 },
    ],
    compute: (v) => {
      const totalDough = n(v, "loaves", 1) * n(v, "loafweight");
      const hyd = n(v, "hydration", 72) / 100, salt = n(v, "saltpct", 2) / 100;
      const lev = n(v, "levainpct") / 100, yeast = n(v, "yeastpct") / 100;
      // total = flour(1 + hyd + salt + yeast) with levain internally 50/50
      const flourTotal = totalDough / (1 + hyd + salt + yeast);
      const water = flourTotal * hyd;
      const levainWeight = flourTotal * lev;
      const levainFlour = levainWeight / 2;
      const whole = flourTotal * (n(v, "wholepct") / 100);
      const white = flourTotal - whole;
      return {
        cards: [
          { label: "Total dough", value: `${fmt(totalDough)} g`, sub: `${n(v, "loaves")} × ${n(v, "loafweight")} g`, accent: true },
          { label: "Total flour", value: `${fmt(flourTotal)} g`, sub: `${fmt(white)} g white + ${fmt(whole)} g wholegrain` },
          { label: "Total water", value: `${fmt(water)} g`, sub: `${n(v, "hydration")}% hydration` },
          { label: "Salt", value: `${fmt(flourTotal * salt, 1)} g`, sub: `${n(v, "saltpct")}% of flour` },
        ],
        table: {
          title: "Your complete formula",
          head: ["Ingredient", "Baker's %", "Weight", "Added at"],
          rows: [
            ["White flour", `${fmt(100 - n(v, "wholepct"))}%`, `${fmt(white)} g`, "Autolyse"],
            ["Wholegrain flour", `${n(v, "wholepct")}%`, `${fmt(whole)} g`, "Autolyse"],
            ["Water", `${n(v, "hydration")}%`, `${fmt(water)} g`, "Autolyse (hold 5% back)"],
            ["Levain", `${n(v, "levainpct")}%`, `${fmt(levainWeight)} g`, "After autolyse"],
            ["Salt", `${n(v, "saltpct")}%`, `${fmt(flourTotal * salt, 1)} g`, "With final water"],
            ["Commercial yeast", `${n(v, "yeastpct")}%`, `${fmt(flourTotal * yeast, 2)} g`, "With levain"],
            ["Flour inside levain", "—", `${fmt(levainFlour)} g`, "Already counted"],
          ],
        },
        summary: `Hold back about ${fmt(water * 0.05)} g of water as bassinage and add it after the gluten has developed.`,
      };
    },
    intro:
      "Working backwards from a target dough weight is how bakeries plan production: you know you need six 900 gram loaves, so you need 5,400 grams of dough, and every ingredient follows from the percentages. This formula builder inverts the usual arithmetic so the batch always comes out to the loaf count you actually want.",
    sections: [
      { h: "Reading a baker's formula", body: "Flour is always one hundred percent, and every other ingredient is expressed relative to it. A formula reading 100 / 72 / 2 / 20 means flour, seventy-two percent water, two percent salt, twenty percent levain. Percentages therefore sum to more than one hundred, which is correct and intentional." },
      { h: "Autolyse and bassinage", body: "Mixing flour and most of the water and resting for thirty to sixty minutes lets enzymes begin breaking down starch and the gluten network begin forming without kneading. Holding back roughly five percent of the water as bassinage lets you adjust for flour variability at the end, when you can feel the dough." },
      { h: "Wholegrain changes everything downstream", body: "Bran fragments cut gluten strands and absorb water slowly, so a dough with thirty percent wholegrain feels stiff at mixing and slack an hour later. Increase hydration by about two percent for every ten percent wholegrain added, and shorten bulk fermentation because wholegrain flour ferments faster." },
    ],
    staticTable: {
      title: "Classic bread formulas in baker's percentage",
      head: ["Bread", "Hydration", "Salt", "Levain / yeast", "Notes"],
      rows: [
        ["Country sourdough", "75%", "2.0%", "20% levain", "Overnight bulk"],
        ["Baguette (poolish)", "68%", "2.0%", "0.2% yeast", "Long cold proof"],
        ["Ciabatta", "82%", "2.2%", "0.3% yeast", "Biga preferment"],
        ["Focaccia", "80%", "2.5%", "0.5% yeast", "High oil content"],
        ["Sandwich loaf", "62%", "2.0%", "1.2% yeast", "Milk and butter enriched"],
        ["Bagel", "55%", "2.0%", "0.8% yeast", "Boiled before baking"],
        ["Rye 100%", "80%", "2.0%", "30% rye sour", "No gluten network"],
      ],
    },
    faqs: [
      { q: "How much dough do I need per loaf?", a: "A standard boule uses 800-1,000 g of dough, a batard 750-900 g, a sandwich tin loaf 850-950 g and a baguette 350 g. Multiply by loaf count for the batch total." },
      { q: "How much yeast per kilogram of flour?", a: "For a same-day bake, 8-12 g of fresh yeast or 3-4 g of instant per kilogram. For an overnight cold ferment, drop to 1-2 g of instant per kilogram." },
      { q: "Why do baker's percentages add up to more than 100?", a: "Because flour alone is defined as 100%. Every other ingredient is expressed relative to flour weight, so totals of 195-210% are normal." },
    ],
    related: ["sourdough-hydration-calculator", "recipe-scale-converter", "coffee-to-water-ratio-calculator", "keto-net-carb-calculator"],
  },

  {
    slug: "coffee-to-water-ratio-calculator",
    cluster: "lifestyle",
    name: "Coffee Brew Ratio Tool",
    h1: "Coffee to Water Ratio Calculator (French Press, Pour Over, Espresso)",
    title: "Coffee to Water Ratio Calculator by Brew Method",
    description:
      "Get exact coffee and water weights for French press, V60, Chemex, AeroPress, espresso and cold brew, with bloom volume, grind size and brew time.",
    keywords: [
      "coffee to water ratio calculator french press pour over",
      "how many grams of coffee for 500ml water",
      "golden ratio coffee brewing 1 to 16",
      "cold brew concentrate ratio calculator",
    ],
    inputs: [
      { id: "method", label: "Brew method", type: "select", def: "16", options: [
        { value: "16", label: "Pour over V60 — 1:16" },
        { value: "17", label: "Chemex — 1:17" },
        { value: "15", label: "French press — 1:15" },
        { value: "14", label: "AeroPress — 1:14" },
        { value: "2", label: "Espresso — 1:2" },
        { value: "5", label: "Cold brew concentrate — 1:5" },
        { value: "8", label: "Moka pot — 1:8" },
      ] },
      { id: "solve", label: "I know my", type: "select", def: "water", options: [{ value: "water", label: "Water volume" }, { value: "coffee", label: "Coffee dose" }] },
      { id: "amount", label: "Known amount", type: "number", def: "500", unit: "g / ml", min: 1 },
      { id: "strength", label: "Strength adjustment", type: "select", def: "1", options: [
        { value: "1.1", label: "Lighter (+10% water)" },
        { value: "1", label: "Standard" },
        { value: "0.92", label: "Stronger (−8% water)" },
      ] },
    ],
    compute: (v) => {
      const ratio = n(v, "method", 16) * n(v, "strength", 1);
      const known = n(v, "amount");
      const water = s(v, "solve") === "water" ? known : known * ratio;
      const coffee = s(v, "solve") === "water" ? known / ratio : known;
      const methodName = { 16: "V60 pour over", 17: "Chemex", 15: "French press", 14: "AeroPress", 2: "Espresso", 5: "Cold brew", 8: "Moka pot" }[n(v, "method", 16)] ?? "Pour over";
      const grind = { 16: "Medium-fine", 17: "Medium-coarse", 15: "Coarse", 14: "Medium-fine", 2: "Fine", 5: "Extra coarse", 8: "Fine-medium" }[n(v, "method", 16)] ?? "Medium";
      const time = { 16: "2:45-3:15", 17: "4:00-4:30", 15: "4:00", 14: "1:30-2:00", 2: "25-30 s", 5: "16-20 hours", 8: "4-5 min" }[n(v, "method", 16)] ?? "3:00";
      return {
        cards: [
          { label: "Coffee dose", value: `${fmt(coffee, 1)} g`, sub: `${fmt(coffee / 10, 1)} tbsp approx.`, accent: true },
          { label: "Water", value: `${fmt(water)} g`, sub: `${fmt(water)} ml — 1 g per ml` },
          { label: "Ratio", value: `1:${fmt(ratio, 1)}`, sub: methodName },
          { label: "Brew time", value: time, sub: `${grind} grind` },
        ],
        table: {
          title: "The same water volume across every method",
          head: ["Method", "Ratio", "Coffee needed", "Grind", "Brew time"],
          rows: [
            ["Espresso", "1:2", `${fmt(water / 2, 1)} g`, "Fine", "25-30 s"],
            ["Cold brew concentrate", "1:5", `${fmt(water / 5, 1)} g`, "Extra coarse", "16-20 h"],
            ["Moka pot", "1:8", `${fmt(water / 8, 1)} g`, "Fine-medium", "4-5 min"],
            ["AeroPress", "1:14", `${fmt(water / 14, 1)} g`, "Medium-fine", "1:30-2:00"],
            ["French press", "1:15", `${fmt(water / 15, 1)} g`, "Coarse", "4:00"],
            ["V60 pour over", "1:16", `${fmt(water / 16, 1)} g`, "Medium-fine", "2:45-3:15"],
            ["Chemex", "1:17", `${fmt(water / 17, 1)} g`, "Medium-coarse", "4:00-4:30"],
          ],
        },
        summary: `Bloom with ${fmt(coffee * 2)} g of water — twice the coffee weight — for thirty to forty-five seconds before the main pour.`,
      };
    },
    intro:
      "Brew ratio is the single biggest lever on how coffee tastes, ahead of grind size, water temperature and bean origin. The specialty industry's golden ratio sits between 1:15 and 1:17 by weight for filter methods, which produces an extraction yield near twenty percent and a total dissolved solids reading in the 1.15-1.35 percent band most tasters prefer.",
    sections: [
      { h: "Weigh, do not scoop", body: "A tablespoon of whole beans and a tablespoon of ground coffee differ by nearly forty percent in mass, and roast level changes bean density substantially — dark roasts are lighter for the same volume. A cheap kitchen scale reading to 0.1 grams removes more inconsistency than any equipment upgrade." },
      { h: "Why the bloom matters", body: "Fresh coffee releases carbon dioxide during the first minute of contact with hot water. That gas physically repels water from the grounds and causes uneven extraction. Wetting with roughly twice the coffee weight and waiting thirty to forty-five seconds lets it degas so the main pour extracts evenly." },
      { h: "Diagnosing sour versus bitter", body: "Sour, thin coffee means under-extraction: grind finer, brew longer, or raise water temperature. Harsh, drying bitterness means over-extraction: grind coarser or shorten contact time. Adjust one variable at a time, and change the ratio only once grind and time are dialled in." },
    ],
    staticTable: {
      title: "Brew ratios and target parameters",
      head: ["Method", "Ratio", "Grind", "Water temp", "Total time", "Typical TDS"],
      rows: [
        ["Espresso", "1:2", "Fine", "92-94°C", "25-30 s", "8-12%"],
        ["Moka pot", "1:8", "Fine-medium", "Stovetop", "4-5 min", "3-5%"],
        ["AeroPress", "1:14", "Medium-fine", "80-90°C", "1:30-2:00", "1.4-1.8%"],
        ["French press", "1:15", "Coarse", "93-96°C", "4 min", "1.2-1.5%"],
        ["V60", "1:16", "Medium-fine", "92-96°C", "2:45-3:15", "1.2-1.4%"],
        ["Chemex", "1:17", "Medium-coarse", "93-96°C", "4:00-4:30", "1.1-1.3%"],
        ["Cold brew", "1:5 concentrate", "Extra coarse", "Room temp", "16-20 h", "3-4%"],
      ],
    },
    faqs: [
      { q: "How many grams of coffee for 500 ml of water?", a: "About 31 g at the 1:16 pour-over ratio, 33 g for French press at 1:15, or 29 g for Chemex at 1:17." },
      { q: "What is the golden ratio for coffee?", a: "Between 1:15 and 1:17 coffee to water by weight for filter brewing. Espresso runs far more concentrated at roughly 1:2." },
      { q: "Should I measure coffee by volume or weight?", a: "Always by weight. Bean density varies by roast level and origin, so a scoop can differ by thirty to forty percent in actual mass." },
    ],
    related: ["caffeine-half-life-calculator", "recipe-scale-converter", "bakers-percentage-recipe-calculator", "sourdough-hydration-calculator"],
  },

  {
    slug: "solar-panel-battery-size-calculator",
    cluster: "lifestyle",
    name: "Solar kW & Battery Storage",
    h1: "Off-Grid Solar Panel kW and Battery Storage Size Calculator",
    title: "Solar Panel & Battery Storage Size Calculator (Off-Grid)",
    description:
      "Size a solar array and battery bank from your monthly consumption and peak sun hours, including system losses, days of autonomy and depth of discharge.",
    keywords: [
      "off grid solar panel kw battery storage size calculator",
      "how many solar panels for 1000 kwh per month",
      "battery bank sizing days of autonomy formula",
      "peak sun hours by location solar sizing",
    ],
    inputs: [
      { id: "monthly", label: "Monthly consumption", type: "number", def: "900", unit: "kWh", min: 10, step: 25 },
      { id: "sun", label: "Peak sun hours", type: "number", def: "4.5", unit: "h/day", min: 1, max: 8, step: 0.1 },
      { id: "losses", label: "System efficiency", type: "number", def: "80", unit: "%", min: 50, max: 95 },
      { id: "autonomy", label: "Days of autonomy", type: "number", def: "2", min: 0.5, max: 7, step: 0.5 },
      { id: "dod", label: "Battery depth of discharge", type: "select", def: "90", options: [
        { value: "90", label: "LiFePO4 — 90% usable" },
        { value: "80", label: "Lithium NMC — 80% usable" },
        { value: "50", label: "Lead acid AGM — 50% usable" },
      ] },
      { id: "panel", label: "Panel rating", type: "number", def: "450", unit: "W", min: 100, max: 800, step: 10 },
    ],
    compute: (v) => {
      const daily = n(v, "monthly") / 30.44;
      const eff = n(v, "losses", 80) / 100;
      const arrayKw = daily / (n(v, "sun", 4.5) * eff);
      const panels = Math.ceil((arrayKw * 1000) / n(v, "panel", 450));
      const usableNeeded = daily * n(v, "autonomy", 2);
      const batteryKwh = usableNeeded / (n(v, "dod", 90) / 100);
      return {
        cards: [
          { label: "Array size needed", value: `${fmt(arrayKw, 2)} kW`, sub: `${panels} × ${n(v, "panel")} W panels`, accent: true },
          { label: "Battery bank", value: `${fmt(batteryKwh, 1)} kWh`, sub: `${n(v, "autonomy")} days autonomy at ${n(v, "dod")}% DoD` },
          { label: "Daily consumption", value: `${fmt(daily, 1)} kWh`, sub: `${fmt(daily / 24, 2)} kW average load` },
          { label: "Annual generation", value: `${fmt(arrayKw * n(v, "sun", 4.5) * eff * 365)} kWh`, sub: "At your sun hours" },
        ],
        table: {
          title: "Sizing sensitivity to peak sun hours",
          head: ["Peak sun hours", "Array needed", "Panels", "Typical location"],
          rows: ([[3, "Northern UK, Nordics"], [3.5, "Germany, Belgium"], [4, "Southern UK, NE US"], [4.5, "Central Europe, Midwest US"], [5.5, "Spain, Texas"], [6.5, "Arizona, Gulf, North Africa"]] as [number, string][])
            .map(([sun, loc]) => {
              const kw = daily / (sun * eff);
              return [`${sun} h`, `${fmt(kw, 2)} kW`, Math.ceil((kw * 1000) / n(v, "panel", 450)), loc];
            }),
        },
        summary: `System losses of ${fmt(100 - n(v, "losses", 80))}% cover inverter conversion, wiring, soiling, temperature derate and charge controller inefficiency.`,
      };
    },
    intro:
      "Off-grid sizing has two independent questions. The array must generate enough energy across an average day, which depends on peak sun hours at your latitude. The battery must carry the household through the night plus a run of poor weather, which depends on days of autonomy and how deeply the chemistry can safely discharge.",
    sections: [
      { h: "What peak sun hours actually mean", body: "A peak sun hour is one hour of irradiance at 1,000 watts per square metre. A location with 4.5 peak sun hours receives that much total energy spread across a longer, weaker day. It varies by latitude, season and shading — winter values in northern Europe can be a third of the annual average, which is why off-grid systems are sized on the worst month, not the average." },
      { h: "Why eighty percent efficiency, not one hundred", body: "Inverter conversion costs three to eight percent, wiring and connectors one to three, panel soiling two to five, high-temperature derate five to fifteen in hot climates, and charge controller losses two to five. Combined, a realistic system delivers seventy-five to eighty-five percent of nameplate output." },
      { h: "Depth of discharge decides real battery cost", body: "A 10 kWh lead-acid bank at fifty percent DoD delivers 5 kWh usable; the same nominal capacity in LiFePO4 at ninety percent delivers 9 kWh and survives four to eight times more cycles. Compare batteries on cost per usable kilowatt-hour per cycle, not on nominal capacity." },
    ],
    staticTable: {
      title: "Array size for common household consumption",
      head: ["Monthly use", "Daily use", "Array at 4 sun hours", "Array at 5.5 sun hours", "Battery for 2 days (LiFePO4)"],
      rows: [
        ["300 kWh", "9.9 kWh", "3.1 kW", "2.2 kW", "22 kWh"],
        ["500 kWh", "16.4 kWh", "5.1 kW", "3.7 kW", "36 kWh"],
        ["750 kWh", "24.6 kWh", "7.7 kW", "5.6 kW", "55 kWh"],
        ["900 kWh", "29.6 kWh", "9.2 kW", "6.7 kW", "66 kWh"],
        ["1,200 kWh", "39.4 kWh", "12.3 kW", "9.0 kW", "88 kWh"],
      ],
    },
    faqs: [
      { q: "How many solar panels do I need for 1,000 kWh per month?", a: "About 10.3 kW at four peak sun hours with eighty percent system efficiency — roughly twenty-three panels rated at 450 W each." },
      { q: "How big should my battery bank be?", a: "Multiply daily consumption by your desired days of autonomy, then divide by the usable depth of discharge. Two days at 30 kWh a day with LiFePO4 needs about 66 kWh nominal." },
      { q: "Should I size for winter or the annual average?", a: "Off-grid systems must be sized on the worst month, otherwise you rely on a generator through winter. Grid-tied systems can use the annual average because the grid covers shortfalls." },
    ],
    related: ["appliance-electricity-cost-calculator", "ev-fuel-trip-cost-splitter", "pool-chlorine-dosing-calculator", "car-depreciation-calculator"],
  },

  {
    slug: "pool-chlorine-dosing-calculator",
    cluster: "lifestyle",
    name: "Pool Chlorine Dosing",
    h1: "Pool Liquid Chlorine Dosing Calculator (Gallons and Target FC)",
    title: "Pool Chlorine Dosing Calculator — Liquid & Granular",
    description:
      "Calculate exactly how much liquid chlorine, cal-hypo or dichlor to add to reach your target free chlorine level, including shock dosing and CYA guidance.",
    keywords: [
      "pool liquid chlorine dosing calculator gallons pool size",
      "how much bleach to raise chlorine 1 ppm",
      "shock dose calculator by cyanuric acid level",
      "cal hypo vs liquid chlorine dosing amounts",
    ],
    inputs: [
      { id: "volume", label: "Pool volume", type: "number", def: "15000", unit: "gallons", min: 100, step: 500 },
      { id: "current", label: "Current free chlorine", type: "number", def: "1", unit: "ppm", min: 0, max: 30, step: 0.5 },
      { id: "target", label: "Target free chlorine", type: "number", def: "4", unit: "ppm", min: 0.5, max: 40, step: 0.5 },
      { id: "product", label: "Chlorine product", type: "select", def: "10", options: [
        { value: "10", label: "Liquid chlorine 10% (pool grade)" },
        { value: "12.5", label: "Liquid chlorine 12.5%" },
        { value: "6", label: "Household bleach 6%" },
        { value: "65", label: "Cal-hypo granular 65%" },
        { value: "56", label: "Dichlor granular 56%" },
      ] },
      { id: "cya", label: "Cyanuric acid (stabiliser)", type: "number", def: "40", unit: "ppm", min: 0, max: 120, step: 5 },
    ],
    compute: (v) => {
      const gal = n(v, "volume"), rise = Math.max(0, n(v, "target") - n(v, "current"));
      const pct = n(v, "product", 10);
      const isLiquid = pct <= 12.5;
      // Liquid: fl oz = (ppm × gallons) / (0.7 × %strength × 100) approximation; use standard: 
      const liquidOz = (rise * gal * 0.0128) / (pct / 10);
      const dryOz = (rise * gal * 0.0013) / (pct / 65);
      const minFc = n(v, "cya") * 0.075;
      const targetFc = n(v, "cya") * 0.115;
      const shockFc = n(v, "cya") * 0.4;
      return {
        cards: [
          { label: isLiquid ? "Liquid chlorine to add" : "Granular chlorine to add", value: isLiquid ? `${fmt(liquidOz)} fl oz` : `${fmt(dryOz, 1)} oz`, sub: isLiquid ? `${fmt(liquidOz / 128, 2)} US gallons` : `${fmt(dryOz / 16, 2)} lb`, accent: true },
          { label: "FC increase", value: `${fmt(rise, 1)} ppm`, sub: `${n(v, "current")} → ${n(v, "target")} ppm` },
          { label: "Ideal FC for your CYA", value: `${fmt(targetFc, 1)} ppm`, sub: `Minimum ${fmt(minFc, 1)} ppm at CYA ${n(v, "cya")}` },
          { label: "SLAM / shock level", value: `${fmt(shockFc, 1)} ppm`, sub: "40% of CYA for algae treatment" },
        ],
        table: {
          title: "Dose for other targets in this pool",
          head: ["Raise FC by", "Liquid 10% (fl oz)", "Liquid 12.5% (fl oz)", "Cal-hypo 65% (oz)", "Bleach 6% (fl oz)"],
          rows: [1, 2, 3, 5, 10].map((x) => [
            `${x} ppm`,
            fmt(x * gal * 0.0128),
            fmt(x * gal * 0.0128 / 1.25),
            fmt(x * gal * 0.0013, 1),
            fmt(x * gal * 0.0128 / 0.6),
          ]),
        },
        summary: "Add chlorine after sunset with the pump running — UV destroys unstabilised chlorine within hours in direct sunlight.",
      };
    },
    intro:
      "Chlorine dosing is a ratio problem tied to a second, less obvious variable: cyanuric acid. CYA protects chlorine from ultraviolet breakdown but also binds it, so the free chlorine level that sanitises a pool with 30 ppm CYA is dangerously low in a pool with 80 ppm. Any dose calculation without a CYA reading is guesswork.",
    sections: [
      { h: "The FC to CYA relationship", body: "The widely used rule keeps free chlorine at roughly seven and a half percent of the CYA level as an absolute minimum and eleven to twelve percent as a target. At CYA 40 that is 3 ppm minimum and 4.6 ppm target; at CYA 80 it is 6 ppm minimum and 9 ppm target. Pools that keep 'the usual 3 ppm' regardless of CYA are the ones that go green every August." },
      { h: "Which product to use when", body: "Liquid chlorine adds no CYA and no calcium, which makes it the default for routine dosing. Cal-hypo adds calcium hardness, useful in soft-water areas and problematic in hard-water ones. Dichlor and trichlor tablets add CYA with every dose, which is convenient early in a season and the main cause of runaway stabiliser levels by the end of it." },
      { h: "Timing and circulation", body: "Add chlorine in the evening with the pump running, pouring slowly around the deep end perimeter. Daytime dosing in an unstabilised or lightly stabilised pool can lose half the addition to UV within a few hours. Retest no sooner than fifteen minutes of full circulation." },
    ],
    staticTable: {
      title: "Target chlorine levels by cyanuric acid",
      head: ["CYA (ppm)", "Minimum FC", "Target FC", "SLAM / shock FC", "Notes"],
      rows: [
        [20, "1.5", "2.5", "8", "Low protection, fast UV loss"],
        [30, "2.0", "3.5", "12", "Common indoor / spring level"],
        [40, "3.0", "4.5", "16", "Typical outdoor target"],
        [50, "3.5", "6.0", "20", "Sunny climates"],
        [70, "5.0", "8.0", "28", "High — consider partial drain"],
        [90, "7.0", "10.0", "36", "Too high for most owners"],
      ],
    },
    faqs: [
      { q: "How much liquid chlorine raises free chlorine by 1 ppm?", a: "Roughly 10.7 fl oz of 10% liquid chlorine per 10,000 gallons. A 15,000 gallon pool needs about 16 fl oz per ppm." },
      { q: "Why does my chlorine disappear so fast?", a: "Usually low cyanuric acid letting UV destroy it, or a chlorine demand from algae or organic load. Check CYA first, then run an overnight chlorine loss test." },
      { q: "Can I use household bleach in a pool?", a: "Yes, if it is unscented and additive-free. At 6% strength you need about twice the volume of 10-12.5% pool-grade liquid chlorine." },
    ],
    related: ["solar-panel-battery-size-calculator", "appliance-electricity-cost-calculator", "houseplant-watering-schedule-calculator", "ev-fuel-trip-cost-splitter"],
  },

  {
    slug: "international-shoe-size-converter",
    cluster: "lifestyle",
    name: "Global Shoe & Clothing Converter",
    h1: "International Shoe Size Converter (US, UK, EU, JP Chart)",
    title: "International Shoe Size Converter — US, UK, EU, JP",
    description:
      "Convert shoe sizes between US, UK, EU, Japanese and centimetre foot-length systems for men, women and children, with a fit and width guide.",
    keywords: [
      "international shoe size converter us uk eu jp chart tool",
      "us to uk shoe size conversion mens womens",
      "eu shoe size to cm foot length",
      "japanese shoe size conversion chart",
    ],
    inputs: [
      { id: "gender", label: "Sizing system", type: "select", def: "men", options: [
        { value: "men", label: "Men's" }, { value: "women", label: "Women's" }, { value: "kids", label: "Children's" },
      ] },
      { id: "from", label: "Size you know", type: "select", def: "US", options: ["US", "UK", "EU", "JP", "CM"].map((x) => ({ value: x, label: x === "CM" ? "Foot length (cm)" : x })) },
      { id: "value", label: "Size value", type: "number", def: "10", min: 0.5, max: 60, step: 0.5 },
    ],
    compute: (v) => {
      const g = s(v, "gender", "men");
      const val = n(v, "value");
      // Convert everything to foot length in cm first.
      const usToCm = (x: number) => (g === "women" ? 20.1 + (x - 5) * 0.847 : g === "kids" ? 12.2 + x * 0.847 : 22.6 + (x - 5) * 0.847);
      let cm: number;
      switch (s(v, "from", "US")) {
        case "CM": cm = val; break;
        case "JP": cm = val; break;
        case "EU": cm = (val - 1.5) / 1.5 + 0; cm = (val / 1.5) - 1.5 + 1.5; cm = val * 0.667 - 1.0; break;
        case "UK": cm = usToCm(val + (g === "women" ? 2 : 1)); break;
        default: cm = usToCm(val);
      }
      const us = g === "women" ? (cm - 20.1) / 0.847 + 5 : g === "kids" ? (cm - 12.2) / 0.847 : (cm - 22.6) / 0.847 + 5;
      const uk = us - (g === "women" ? 2 : 1);
      const eu = (cm + 1.0) / 0.667;
      const half = (x: number) => Math.round(x * 2) / 2;
      return {
        cards: [
          { label: "US size", value: fmt(half(us), 1), sub: `${g} sizing`, accent: true },
          { label: "UK size", value: fmt(half(uk), 1), sub: g === "women" ? "US minus 2" : "US minus 1" },
          { label: "EU size", value: fmt(Math.round(eu), 0), sub: "Paris point system" },
          { label: "Foot length", value: `${fmt(cm, 1)} cm`, sub: `JP size ${fmt(half(cm), 1)}` },
        ],
        table: {
          title: "Conversion around your size",
          head: ["US", "UK", "EU", "JP / cm", "Inches"],
          rows: [-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2].map((d) => {
            const u = half(us) + d;
            const c2 = g === "women" ? 20.1 + (u - 5) * 0.847 : g === "kids" ? 12.2 + u * 0.847 : 22.6 + (u - 5) * 0.847;
            return [fmt(u, 1), fmt(u - (g === "women" ? 2 : 1), 1), fmt(Math.round((c2 + 1.0) / 0.667), 0), fmt(c2, 1), fmt(c2 / 2.54, 2)];
          }),
        },
        summary: "Measure your foot in the evening while standing — feet lengthen up to 4% through the day and under load.",
      };
    },
    intro:
      "There is no international standard for shoe sizing. The US and UK use barleycorn increments of one third of an inch from different zero points, continental Europe uses Paris points of two thirds of a centimetre, and Japan simply states foot length in centimetres — which is the only system that describes the foot rather than the last.",
    sections: [
      { h: "How to measure your foot properly", body: "Stand on a sheet of paper against a wall in the evening, heel touching the wall, and mark the longest toe. Measure both feet — a difference of half a size is common — and buy for the larger. Add roughly one centimetre of toe room for walking shoes and one and a half for running." },
      { h: "Why the same size fits differently", body: "Sizing describes the last, not the foot. Italian dress lasts run narrow and long, American athletic lasts run wide and short, and Japanese brands often build for a higher instep. Two shoes both labelled EU 43 can differ by a full centimetre of internal length between brands." },
      { h: "Width is the missing dimension", body: "US sizing uses letter widths from AA narrow through B, D standard, EE and EEEE. UK uses F, G and H fittings. Most European brands sell one width only. If a correctly sized shoe pinches across the ball of the foot, the answer is a wider fitting, not a longer shoe." },
    ],
    staticTable: {
      title: "Men's shoe size conversion chart",
      head: ["US men", "UK", "EU", "JP / cm", "Inches"],
      rows: [
        ["7", "6", "40", "25.0", "9.84"],
        ["8", "7", "41", "25.8", "10.16"],
        ["9", "8", "42", "26.7", "10.51"],
        ["10", "9", "43", "27.5", "10.83"],
        ["11", "10", "44", "28.3", "11.14"],
        ["12", "11", "45", "29.2", "11.50"],
        ["13", "12", "46", "30.0", "11.81"],
        ["14", "13", "47", "30.8", "12.13"],
      ],
    },
    faqs: [
      { q: "How do US and UK shoe sizes differ?", a: "UK sizes run one number below US men's and two below US women's. A US men's 10 is a UK 9; a US women's 8 is a UK 6." },
      { q: "What EU size is a US 10?", a: "Approximately EU 43 in men's sizing, corresponding to a foot length of about 27.5 cm." },
      { q: "Should I size up for running shoes?", a: "Usually by half a size. Feet swell during sustained running and need roughly one and a half centimetres of clearance at the toe to avoid nail damage on descents." },
    ],
    related: ["recipe-scale-converter", "storage-unit-converter", "ideal-body-weight-calculator", "houseplant-watering-schedule-calculator"],
  },

  {
    slug: "houseplant-watering-schedule-calculator",
    cluster: "lifestyle",
    name: "Houseplant Water Schedule",
    h1: "Houseplant Watering Schedule Calculator (Pot Size, Light and Season)",
    title: "Houseplant Watering Schedule Calculator by Species",
    description:
      "Get a watering interval tailored to plant type, pot material and size, light exposure, humidity and season, with volume per watering and overwatering signs.",
    keywords: [
      "houseplant watering schedule calculator humidity pot size",
      "how often to water a monstera in winter",
      "terracotta vs plastic pot watering frequency",
      "signs of overwatering vs underwatering houseplants",
    ],
    inputs: [
      { id: "plant", label: "Plant type", type: "select", def: "7", options: [
        { value: "14", label: "Succulent / cactus" },
        { value: "12", label: "Snake plant / ZZ plant" },
        { value: "10", label: "Rubber plant / dracaena" },
        { value: "7", label: "Monstera / philodendron" },
        { value: "6", label: "Pothos / spider plant" },
        { value: "5", label: "Calathea / maranta" },
        { value: "4", label: "Fern / peace lily" },
      ] },
      { id: "pot", label: "Pot material", type: "select", def: "1", options: [
        { value: "0.75", label: "Terracotta (dries fastest)" },
        { value: "1", label: "Glazed ceramic" },
        { value: "1.15", label: "Plastic (holds moisture)" },
      ] },
      { id: "size", label: "Pot diameter", type: "number", def: "18", unit: "cm", min: 5, max: 60 },
      { id: "light", label: "Light exposure", type: "select", def: "1", options: [
        { value: "0.8", label: "Bright direct sun" },
        { value: "1", label: "Bright indirect" },
        { value: "1.3", label: "Medium / partial shade" },
        { value: "1.6", label: "Low light" },
      ] },
      { id: "season", label: "Season", type: "select", def: "1", options: [
        { value: "0.8", label: "Summer — active growth" },
        { value: "1", label: "Spring / autumn" },
        { value: "1.5", label: "Winter — dormancy" },
      ] },
      { id: "humidity", label: "Room humidity", type: "select", def: "1", options: [
        { value: "1.2", label: "High (60%+, bathroom)" },
        { value: "1", label: "Average (40-60%)" },
        { value: "0.85", label: "Low (under 40%, heated)" },
      ] },
    ],
    compute: (v) => {
      const base = n(v, "plant", 7);
      const sizeFactor = 0.75 + (n(v, "size", 18) / 18) * 0.25;
      const days = base * n(v, "pot", 1) * n(v, "light", 1) * n(v, "season", 1) * n(v, "humidity", 1) * sizeFactor;
      const litres = Math.PI * Math.pow(n(v, "size", 18) / 2, 2) * (n(v, "size", 18) * 0.8) / 1000 / 1000;
      const waterMl = litres * 1000 * 0.25;
      return {
        cards: [
          { label: "Water every", value: `${fmt(days, 0)} days`, sub: `${fmt(30.44 / Math.max(days, 1), 1)} times per month`, accent: true },
          { label: "Water volume", value: `${fmt(Math.max(80, waterMl))} ml`, sub: "About 25% of pot volume" },
          { label: "Soil volume", value: `${fmt(litres, 1)} L`, sub: `${n(v, "size")} cm pot` },
          { label: "Next check", value: `${fmt(days * 0.7, 0)} days`, sub: "Finger test before watering" },
        ],
        table: {
          title: "How each factor shifts the interval",
          head: ["Factor", "Your setting", "Effect on interval"],
          rows: [
            ["Plant type", `${fmt(base)} day baseline`, "Sets the starting point"],
            ["Pot material", n(v, "pot", 1) < 1 ? "Terracotta" : n(v, "pot", 1) > 1 ? "Plastic" : "Ceramic", `${n(v, "pot", 1) < 1 ? "−25%" : n(v, "pot", 1) > 1 ? "+15%" : "neutral"}`],
            ["Light", `${n(v, "light", 1)}× factor`, n(v, "light", 1) < 1 ? "Dries faster" : "Dries slower"],
            ["Season", `${n(v, "season", 1)}× factor`, n(v, "season", 1) > 1 ? "Longer gaps in dormancy" : "Shorter gaps in growth"],
            ["Humidity", `${n(v, "humidity", 1)}× factor`, n(v, "humidity", 1) > 1 ? "Slower drying" : "Faster drying"],
            ["Pot size", `${n(v, "size")} cm`, "Bigger pots hold water longer"],
          ],
        },
        summary: "Always confirm with the finger test — push two to three centimetres into the soil and water only if it is dry at that depth.",
      };
    },
    intro:
      "More houseplants die from overwatering than from anything else, and almost always because the owner followed a fixed weekly schedule instead of the soil. Watering frequency is the product of six variables — species, pot material, pot size, light, humidity and season — and changing any one of them can double or halve the correct interval.",
    sections: [
      { h: "Why terracotta changes everything", body: "Unglazed terracotta is porous and loses moisture through the pot wall as well as the soil surface, typically drying twenty to thirty percent faster than glazed ceramic and forty percent faster than plastic. It is the safest choice for anyone who tends to overwater, and the worst choice for ferns and calatheas." },
      { h: "Winter dormancy halves demand", body: "Light levels and day length fall, growth slows or stops, and water uptake drops accordingly — often by forty to sixty percent. Central heating pulls in the other direction by drying the air. The net effect for most foliage plants is a fifty percent longer interval from November through February." },
      { h: "Reading overwatering versus underwatering", body: "Both produce drooping leaves, which is why people water a drowning plant. Overwatering shows yellowing lower leaves, soft brown stem bases, fungus gnats and consistently wet soil. Underwatering shows crisp brown leaf edges, dry lightweight pots and soil pulling away from the sides." },
    ],
    staticTable: {
      title: "Baseline watering intervals by plant",
      head: ["Plant", "Summer", "Winter", "Light preference", "Overwatering risk"],
      rows: [
        ["Cactus / succulent", "14-21 days", "30-45 days", "Direct sun", "Very high"],
        ["Snake plant", "12-18 days", "25-35 days", "Tolerates low", "Very high"],
        ["ZZ plant", "14-20 days", "28-40 days", "Tolerates low", "Very high"],
        ["Rubber plant", "8-12 days", "14-20 days", "Bright indirect", "Moderate"],
        ["Monstera", "7-10 days", "12-16 days", "Bright indirect", "Moderate"],
        ["Pothos", "6-9 days", "10-14 days", "Any", "Low"],
        ["Calathea", "4-6 days", "7-10 days", "Medium indirect", "Low"],
        ["Fern / peace lily", "3-5 days", "6-9 days", "Medium indirect", "Very low"],
      ],
    },
    faqs: [
      { q: "How often should I water my houseplants?", a: "It depends on species, pot and conditions rather than the calendar. Most foliage plants need water every seven to ten days in summer and every twelve to eighteen in winter — always confirmed by testing the soil first." },
      { q: "How much water should I give each time?", a: "Enough to run freely from the drainage holes, roughly a quarter of the pot volume. Partial watering leaves the lower root zone dry and encourages shallow rooting." },
      { q: "What are the signs of overwatering?", a: "Yellow lower leaves, mushy stem bases, a persistent damp smell, fungus gnats and soil that never dries within the expected interval." },
    ],
    related: ["pool-chlorine-dosing-calculator", "cat-age-to-human-years-converter", "recipe-scale-converter", "solar-panel-battery-size-calculator"],
  },

  {
    slug: "car-depreciation-calculator",
    cluster: "lifestyle",
    name: "Vehicle Depreciation",
    h1: "Car Depreciation Calculator (Year-by-Year Value Curve)",
    title: "Car Depreciation Calculator — Year by Year Value",
    description:
      "Project a car's resale value each year from purchase price, age, mileage and condition, with total depreciation cost per year and per mile.",
    keywords: [
      "car depreciation calculator year by year value curve",
      "how much does a new car lose in the first year",
      "car value after 5 years calculator",
      "cost of depreciation per mile driven",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: [{ value: "$", label: "USD $" }, { value: "£", label: "GBP £" }, { value: "€", label: "EUR €" }, { value: "A$", label: "AUD A$" }] },
      { id: "price", label: "Purchase price", type: "number", def: "38000", min: 500, step: 500 },
      { id: "age", label: "Age at purchase", type: "number", def: "0", unit: "years", min: 0, max: 20 },
      { id: "hold", label: "Years you will keep it", type: "number", def: "5", min: 1, max: 20 },
      { id: "miles", label: "Annual mileage", type: "number", def: "12000", min: 0, step: 1000 },
      { id: "segment", label: "Vehicle segment", type: "select", def: "1", options: [
        { value: "0.82", label: "Luxury saloon (fast depreciation)" },
        { value: "1", label: "Mainstream family car" },
        { value: "1.12", label: "Pickup / SUV (holds value)" },
        { value: "1.18", label: "Hybrid / desirable niche" },
        { value: "0.88", label: "Electric (battery-era discount)" },
      ] },
      { id: "condition", label: "Condition grade", type: "select", def: "1", options: [
        { value: "1.05", label: "Excellent — full history" },
        { value: "1", label: "Good — normal wear" },
        { value: "0.9", label: "Fair — cosmetic damage" },
      ] },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const price = n(v, "price"), seg = n(v, "segment", 1), cond = n(v, "condition", 1);
      const startAge = n(v, "age");
      const valueAt = (age: number) => {
        // Year 1 ~20% drop, then ~15% of remaining annually, flattening after year 8.
        let val = price;
        for (let y = 1; y <= age; y++) {
          const rate = y === 1 ? 0.2 : y <= 5 ? 0.15 : y <= 9 ? 0.1 : 0.06;
          val *= 1 - rate / (seg * cond);
        }
        return val;
      };
      const mileagePenalty = Math.max(0.7, 1 - Math.max(0, n(v, "miles") - 10000) / 100000);
      const endAge = startAge + n(v, "hold", 1);
      const endValue = valueAt(endAge - startAge) * mileagePenalty;
      const loss = price - endValue;
      const rows: (string | number)[][] = [];
      for (let y = 1; y <= Math.min(10, n(v, "hold", 1) + 3); y++) {
        const val = valueAt(y) * Math.max(0.7, 1 - Math.max(0, n(v, "miles") * y - 10000 * y) / 100000);
        rows.push([`Year ${y}`, money(val, c), money(price - val, c), `${fmt((val / price) * 100, 1)}%`]);
      }
      return {
        cards: [
          { label: `Value after ${n(v, "hold")} years`, value: money(endValue, c), sub: `${fmt((endValue / price) * 100, 1)}% of purchase price`, accent: true },
          { label: "Total depreciation", value: money(loss, c), sub: `${fmt((loss / price) * 100, 1)}% lost` },
          { label: "Cost per year", value: money(loss / Math.max(1, n(v, "hold", 1)), c), sub: "Depreciation only" },
          { label: "Cost per mile", value: money(loss / Math.max(1, n(v, "miles") * n(v, "hold", 1)), c, 3), sub: "Before fuel and servicing" },
        ],
        table: { title: "Year-by-year value curve", head: ["Year", "Estimated value", "Cumulative loss", "% of original"], rows },
        summary: "Depreciation is almost always the largest single cost of car ownership — usually more than fuel, insurance and servicing combined.",
      };
    },
    intro:
      "Depreciation is the cost most owners never see on a statement, and it dwarfs the ones they do. A typical new car loses about twenty percent of its value in the first twelve months and roughly sixty percent over five years, which on a mid-priced family car is more than fuel, insurance, tax and servicing added together.",
    sections: [
      { h: "Why the first year is so brutal", body: "The moment a car is registered it moves from the new market to the used market, and the new-car premium — dealer margin, delivery, registration and the value buyers place on being first — disappears immediately. That is why a twelve to eighteen month old car with delivery mileage is consistently the best value purchase in the market." },
      { h: "What holds value and what does not", body: "Pickups, body-on-frame SUVs, manual sports cars and hybrids with strong reputations resist depreciation. Large luxury saloons, cars with expensive optional extras, and models facing imminent replacement fall fastest. Colour matters more than people expect — unusual colours can cost several percent at resale." },
      { h: "Mileage versus age", body: "Below about ten thousand miles a year, age drives value more than mileage. Above fifteen thousand, mileage begins to dominate and the penalty accelerates as the car crosses psychological thresholds at 60,000 and 100,000 miles. Service history closes part of that gap, particularly on premium brands." },
    ],
    staticTable: {
      title: "Typical retained value by segment",
      head: ["Segment", "After 1 yr", "After 3 yrs", "After 5 yrs", "After 8 yrs"],
      rows: [
        ["Luxury saloon", "72%", "48%", "33%", "18%"],
        ["Mainstream family car", "80%", "58%", "42%", "26%"],
        ["Compact hatchback", "82%", "61%", "45%", "29%"],
        ["SUV / crossover", "84%", "64%", "49%", "32%"],
        ["Pickup truck", "87%", "70%", "56%", "39%"],
        ["Hybrid", "85%", "66%", "51%", "34%"],
        ["Electric vehicle", "76%", "52%", "37%", "22%"],
      ],
    },
    faqs: [
      { q: "How much does a new car lose in the first year?", a: "Typically fifteen to twenty-five percent, with luxury models at the higher end and pickups and hybrids at the lower end." },
      { q: "What is a car worth after 5 years?", a: "Around forty to fifty percent of purchase price for mainstream models, thirty-three to thirty-eight percent for luxury saloons and electric vehicles, and over fifty-five percent for pickups." },
      { q: "Is buying a two year old car better value?", a: "Usually yes. The steepest part of the depreciation curve has already passed, manufacturer warranty often remains, and the effective cost per year of ownership is materially lower." },
    ],
    related: ["ev-fuel-trip-cost-splitter", "inflation-purchasing-power-calculator", "mortgage-extra-payment-calculator", "compound-interest-visualizer"],
  },

  {
    slug: "fasting-electrolyte-calculator",
    cluster: "lifestyle",
    name: "Fasting Water & Electrolyte Needs",
    h1: "Water Fasting Hydration and Electrolyte Requirement Calculator",
    title: "Fasting Electrolyte & Hydration Requirement Calculator",
    description:
      "Calculate daily water, sodium, potassium and magnesium needs during a fast based on body weight, fasting hours and climate, with a DIY electrolyte recipe.",
    keywords: [
      "water fasting hydration electrolyte requirement calculator",
      "how much salt during a water fast",
      "keto flu electrolyte dosage sodium potassium magnesium",
      "diy electrolyte drink recipe for fasting",
    ],
    inputs: [
      { id: "weight", label: "Body weight", type: "number", def: "80", unit: "kg", min: 35, max: 250 },
      { id: "hours", label: "Fasting duration", type: "number", def: "48", unit: "hours", min: 12, max: 336 },
      { id: "climate", label: "Climate / activity", type: "select", def: "1", options: [
        { value: "0.9", label: "Cool and sedentary" },
        { value: "1", label: "Temperate, light activity" },
        { value: "1.25", label: "Warm or active" },
        { value: "1.5", label: "Hot climate or training" },
      ] },
      { id: "sauna", label: "Sauna or heavy sweating", type: "select", def: "0", options: [{ value: "0", label: "None" }, { value: "1", label: "Occasional" }, { value: "2", label: "Daily" }] },
    ],
    compute: (v) => {
      const kg = n(v, "weight"), cl = n(v, "climate", 1), days = n(v, "hours", 24) / 24;
      const water = kg * 0.035 * cl + n(v, "sauna") * 0.5;
      const sodium = 3500 * cl + n(v, "sauna") * 700;
      const potassium = 1500 + kg * 8;
      const magnesium = 350 + (kg > 90 ? 50 : 0);
      return {
        cards: [
          { label: "Water per day", value: `${fmt(water, 1)} L`, sub: `${fmt(water * 33.8)} fl oz`, accent: true },
          { label: "Sodium per day", value: `${fmt(sodium)} mg`, sub: `${fmt(sodium / 388, 1)} tsp of salt` },
          { label: "Potassium per day", value: `${fmt(potassium)} mg`, sub: "Potassium chloride or citrate" },
          { label: "Magnesium per day", value: `${fmt(magnesium)} mg`, sub: "Glycinate or citrate form" },
        ],
        table: {
          title: "Full protocol for this fast",
          head: ["Nutrient", "Per day", `Total over ${fmt(days, 1)} days`, "Practical source"],
          rows: [
            ["Water", `${fmt(water, 1)} L`, `${fmt(water * days, 1)} L`, "Sip steadily, not in boluses"],
            ["Sodium", `${fmt(sodium)} mg`, `${fmt(sodium * days)} mg`, `${fmt(sodium / 388, 1)} tsp unrefined salt`],
            ["Potassium", `${fmt(potassium)} mg`, `${fmt(potassium * days)} mg`, "Lite salt / KCl, divided doses"],
            ["Magnesium", `${fmt(magnesium)} mg`, `${fmt(magnesium * days)} mg`, "Glycinate at night"],
          ],
        },
        summary: `DIY mix per litre: ${fmt(sodium / Math.max(water, 1) / 388, 2)} tsp salt, a pinch of potassium chloride and 100 mg magnesium.`,
      };
    },
    intro:
      "Almost every unpleasant symptom attributed to fasting — headache, dizziness, cramps, heart palpitations, crushing fatigue — is an electrolyte problem, not a calorie problem. When insulin falls, the kidneys switch from retaining sodium to excreting it rapidly, and water follows. Replacing sodium, potassium and magnesium turns a miserable fast into an uneventful one.",
    sections: [
      { h: "The natriuresis of fasting", body: "Within twenty-four hours of stopping food, insulin drops far enough that renal sodium reabsorption falls sharply. Sodium losses of three to five grams a day are typical in the first seventy-two hours. Drinking plain water in that state dilutes the remaining electrolytes further and can produce genuine hyponatraemia — which is why plain-water-only fasting protocols cause the worst symptoms." },
      { h: "Matching the symptom to the mineral", body: "Headache, light-headedness on standing and profound fatigue usually indicate sodium depletion. Muscle cramps and palpitations point to potassium or magnesium. Constipation, poor sleep and eye twitching are classic magnesium signals. Correcting the specific mineral resolves the symptom within a few hours in most cases." },
      { h: "Safety limits and who should not fast", body: "Potassium supplementation carries real cardiac risk at high doses and should stay well within standard dietary amounts, spread through the day. Anyone taking diuretics, ACE inhibitors, SGLT2 inhibitors, lithium or insulin, and anyone with kidney or heart disease, pregnancy or a history of eating disorders should not fast without medical supervision." },
    ],
    staticTable: {
      title: "Electrolyte targets during fasting",
      head: ["Electrolyte", "Daily target", "Deficiency symptoms", "Common source", "Caution"],
      rows: [
        ["Sodium", "3,000-5,000 mg", "Headache, dizziness, fatigue", "Unrefined salt in water", "Raise slowly if hypertensive"],
        ["Potassium", "1,000-3,500 mg", "Cramps, palpitations, weakness", "Potassium chloride / lite salt", "Never bolus large doses"],
        ["Magnesium", "300-500 mg", "Cramps, poor sleep, twitching", "Glycinate or citrate", "Citrate is laxative at high dose"],
        ["Calcium", "Usually adequate", "Rare in short fasts", "—", "Monitor in extended fasts"],
        ["Water", "30-40 ml per kg", "Dark urine, dry mouth", "Sipped steadily", "Excess plain water dilutes sodium"],
      ],
    },
    faqs: [
      { q: "How much salt should I take during a water fast?", a: "Roughly three to five grams of sodium a day — about one and a half to two teaspoons of salt — divided across the day and dissolved in water rather than taken at once." },
      { q: "Can I drink too much water while fasting?", a: "Yes. Without food-derived sodium, excessive plain water can dilute blood sodium and cause hyponatraemia. Sip to thirst and always include electrolytes." },
      { q: "What causes keto flu and fasting headaches?", a: "Predominantly sodium loss driven by falling insulin, compounded by magnesium and potassium depletion. Replacing all three usually resolves symptoms within hours." },
    ],
    related: ["water-fasting-weight-loss-calculator", "keto-net-carb-calculator", "tdee-calculator-weight-loss", "caffeine-half-life-calculator"],
  },
];